import { SdkAcdAgent } from '../sdk-acd/sdk-acd.service';
import { NiceSdkAuthenticationService } from '../sdk-auth/nice-sdk-authentication.service';
import { LoggerService } from 'src/app/core/services/logger.service';
import { CXoneVoiceContact } from '@nice-devone/acd-sdk';
import { Injectable } from '@angular/core';
import { EventHandlerService } from '../../services/event-handler.service';
import {
  CXonePopUrl,
  EndSessionRequest,
  VoiceContactStatus,
} from '@nice-devone/common-sdk';
import { ContatoEvento } from 'src/app/shared/models/contato-evento.models';
import { CxOneUtils } from '../util/CxOneUtils';
import { AgentStateEventDTO } from 'src/app/components/pessoa-fisica/domain/ContactCenter/AgentStateEventDTO';

import { SdkDigitalService } from '../sdk-digital/sdk-digital.service';
import {
  CXoneDigitalClient,
  CXoneDigitalContact,
} from '@nice-devone/digital-sdk';
import { IContactCenter } from '../interfaces/IContactCenter';
import {
  VoiceCallEventType,
  VoiceEventDTO,
} from '../DataTransferObjects/VoiceEventDTO';
import {
  FocusEventSource,
  OnFocusEventDTO,
} from '../DataTransferObjects/OnFocusEventDTO';
import {
  DigitalEventDTO,
  DigitalSessionDirection,
  DigitalSessionEndReason,
  DigitalSessionEventType,
} from '../DataTransferObjects/DigitalEventDTO';
import { Subject } from 'rxjs';
import { getMediaType } from 'src/app/utils/cxone-utils';

@Injectable({
  providedIn: 'root',
})
export class ContactCenterPjService implements IContactCenter {
  public onVoiceCallEvent$ = new Subject<VoiceEventDTO>();
  public onAgentStatusEvent$ = new Subject<AgentStateEventDTO>();
  public onDigitalSessionEvent$ = new Subject<DigitalEventDTO>();
  public onFocusEvent$ = new Subject<OnFocusEventDTO>();

  private pendingPopUrls: Map<string, CXonePopUrl> = new Map();

  constructor(
    private niceSdkAuthService: NiceSdkAuthenticationService,
    private sdkAcdAgent: SdkAcdAgent,
    private logger: LoggerService,
    private eventHandler: EventHandlerService,
    private sdkDigital: SdkDigitalService,
  ) {}

  public async initializer() {
    await this.niceSdkAuthService.launchAgent();

    await this.sdkAcdAgent.initialize();
    this.sdkAcdAgent.agentState$.subscribe((agentState) => {
      this.AgentStateUpdateHandler(agentState);
    });

    try {
      this.sdkAcdAgent.voiceContactUpdate$.subscribe(
        async (voiceContact: CXoneVoiceContact) => {
          this.VoiceContactUpdateHandler(voiceContact);
        },
      );
    } catch (error) {
      this.logger.warn('Falha inicialização de voz:', error);
    }

    try {
      await this.sdkDigital.initializeEvents();
      CXoneDigitalClient.instance.digitalContactManager.onDigitalContactEvent.subscribe(
        async (digitalContact: CXoneDigitalContact) => {
          this.DigitalContactHandler(digitalContact);
        },
      );
      this.sdkAcdAgent.instance.screenPop.screenPopSubscribe(
        true,
        true,
        true,
        true,
        true,
        true,
      );
      this.sdkAcdAgent.instance.screenPop.popUrlSubject.subscribe(
        (popUrlData: CXonePopUrl) => {
          this.PopUrlHandler(popUrlData);
        },
      );
    } catch (error) {
      this.logger.warn('Falha inicialização de digital:', error);
    }
  }

  public async LogoutUser() {
    let endRequest: EndSessionRequest = {
      forceLogoff: true,
      endContacts: false,
      ignorePersonalQueue: true,
    };

    this.sdkAcdAgent.cxoneAcd.session.endSession(endRequest);
  }

  private VoiceContactUpdateHandler(voiceContact: CXoneVoiceContact) {
    this.logger.info(voiceContact);
    let voiceCallEventType = this.GetVoiceCallEventType(voiceContact);

    if (voiceCallEventType === null) {
      this.logger.info(
        `CxOne VoiceContactUpdate descartado: ContactId ${voiceContact.contactID}.`,
      );
      return;
    }

    this.updateEventHandler(voiceContact, voiceCallEventType);
    let VoiceCallEventDTO: VoiceEventDTO = {
      Status: voiceContact.status,
      MasterId: voiceContact.masterID,
      ContactId: voiceContact.contactID || '',
      Ani: voiceContact.ani || '',
      Dnis: voiceContact.dnis || '',
      AttachData: CxOneUtils.CreateDictionaryFromPopUrl(
        voiceContact.screenPopUrl,
      ),
      isInboundCall: voiceContact.isInbound,
      CallStatus: voiceCallEventType,
      DisconectCode: voiceContact.disconnectCode,
      InteractionId: voiceContact.interactionId,
    };

    this.onVoiceCallEvent$.next(VoiceCallEventDTO);
  }

  private GetVoiceCallEventType(
    voiceContact: CXoneVoiceContact,
  ): VoiceCallEventType | null {
    const status = voiceContact.status || '';

    this.logger.info(
      `Status do contato: ${voiceContact.status}. ContactId: ${voiceContact.contactID}.`,
    );
    if (
      status.toLowerCase() === VoiceContactStatus.ACTIVE &&
      !this.eventHandler.isProcessed({
        id: voiceContact.contactID,
        status: voiceContact.status,
      }) &&
      voiceContact.screenPopUrl !== ''
    ) {
      return VoiceCallEventType.Answered;
    }

    //Lógica para permitir eventos outbound
    if (
      status.toLowerCase() === VoiceContactStatus.ACTIVE &&
      !this.eventHandler.isProcessed({
        id: voiceContact.contactID,
        status: voiceContact.status,
      }) &&
      voiceContact.isInbound !== true
    ) {
      return VoiceCallEventType.Answered;
    }

    if (status.toLowerCase() === VoiceContactStatus.DISCONNECTED) {
      this.logger.info('Evento de release.');
      return VoiceCallEventType.Released;
    }

    return null;
  }

  private updateEventHandler(
    voiceContact: CXoneVoiceContact,
    voiceCallEventType: VoiceCallEventType,
  ) {
    let contatoEvento = {
      id: voiceContact.contactID,
      status: voiceContact.status,
    } as ContatoEvento;

    switch (voiceCallEventType) {
      case VoiceCallEventType.Answered: {
        this.eventHandler.add(contatoEvento);
        break;
      }
      case VoiceCallEventType.Released: {
        this.eventHandler.remove(contatoEvento);
        break;
      }
      default: {
        this.logger.warn('Tipo de evento de voz desconhecido.');
        break;
      }
    }
  }

  private AgentStateUpdateHandler(agentState: any) {
    this.logger.info('PJ (ACD) - Agent State', agentState);

    let agentStateDTO: AgentStateEventDTO = {
      agentNextState: agentState.nextState.reason,
      agentCurrentState: agentState.currentState.state,
    };
    this.onAgentStatusEvent$.next(agentStateDTO);
  }

  private async DigitalContactHandler(digitalContact: CXoneDigitalContact) {
    this.logger.info('Digital contact recebido: ', digitalContact);

    if (digitalContact.eventDetails.eventType === 'CaseInboxAssigned') {
      this.DigitalContactCaseAssignedHandler(digitalContact);
    }

    if (digitalContact.eventDetails.eventType === 'CaseInboxUnassigned') {
      this.DigitalContactCaseUnassignedHandler(digitalContact);
    }
  }

  private async DigitalContactCaseAssignedHandler(
    digitalContact: CXoneDigitalContact,
  ) {
    let popUrl = '';

    let count = 0;
    while (count < 5) {
      popUrl = this.pendingPopUrls.get(digitalContact.caseId)?.url ?? '';

      if (popUrl != '') {
        this.logger.warn(
          'PopUrl encontrado para o contato digital: ',
          digitalContact.caseId,
        );
        break;
      }

      this.logger.warn(
        'PopUrl não encontrado para o contato digital: ',
        digitalContact.caseId,
      );

      await this.delay(1000);
      count++;
    }

    let attachData = CxOneUtils.CreateDictionaryFromPopUrl(popUrl) ?? {};

    let digitalSession: DigitalEventDTO = {
      ContactId: digitalContact.caseId,
      AttachData: attachData,
      SessionDirection: DigitalSessionDirection.Inbound,
      SessionEventType: DigitalSessionEventType.Assigned,
      mediaType: getMediaType(digitalContact),
    };
    this.onDigitalSessionEvent$.next(digitalSession);
  }

  private DigitalContactCaseUnassignedHandler(
    digitalContact: CXoneDigitalContact,
  ) {
    let digitalSession: DigitalEventDTO = {
      ContactId: digitalContact.caseId,
      SessionDirection: DigitalSessionDirection.Inbound,
      SessionEventType: DigitalSessionEventType.Unassigned,
      SessionEndReason: DigitalSessionEndReason.Closed,
      mediaType: getMediaType(digitalContact),
    };
    this.onDigitalSessionEvent$.next(digitalSession);
    return;

    //Transferencia
  }

  private async PopUrlHandler(popUrl: CXonePopUrl) {
    this.logger.info('PopUrl recebido:', popUrl);

    if (popUrl.tabTitle?.toUpperCase() === 'ATTACHESDYNAMICS') {
      this.pendingPopUrls.set(popUrl.contactId, popUrl);
      this.logger.info(
        'Attachdata amazenado para contato digital: ',
        popUrl.contactId,
      );
    }

    if (popUrl.tabTitle?.toUpperCase() === 'FOCUS') {
      let focusEvent: OnFocusEventDTO = {
        Id: popUrl.contactId,
        Source: FocusEventSource.ContactCenter,
      };
      this.onFocusEvent$.next(focusEvent);
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
